import { finishProgressAttempt, isCurrentProgressAttempt } from '../systems/ProgressData';
import Phaser from 'phaser';
import { ProgressData as PlayerData } from '../systems/ProgressData';
import { apiClient } from '../services/ApiClient';
import { SUMA_MISSIONS } from '../planets/additionMissions';
import { RESTA_MISSIONS } from '../planets/subtractionMissions';
import { MULTIPLICACION_MISSIONS } from '../planets/multiplicationMissions';
import { DIVISION_MISSIONS } from '../planets/divisionMissions';
import { mountMenu, element, button, header, rows, go, primaryShortcut } from '../util/menuUI';
import { formatScore } from '../util/numberFormat';
import { createMissionContentAccess } from '../systems/MissionContentAccess';

const MASTERY_ACCURACY = 80;

export class ResultScene extends Phaser.Scene {
  constructor() { super('ResultScene'); }
  create(run) {
    if (!isCurrentProgressAttempt(run.progressAttemptId)) return;
    const audio = this.registry.get('audio');
    if (run.won && audio) {
      audio.playVictory();
      this.events.once('shutdown', () => audio.stopVictory());
    }
    const planetId = this.registry.get('currentPlanet') || 'suma';
    const missions = planetId === 'resta' ? RESTA_MISSIONS : planetId === 'mult' ? MULTIPLICACION_MISSIONS : planetId === 'division' ? DIVISION_MISSIONS : SUMA_MISSIONS;
    const mission = this.registry.get('currentMissionData') || missions[0];
    const isPracticeSession = !!run.practiceSession;
    const isDevPreview = !!run.devPreview;
    // Las prácticas son personalizadas: ayudan a aprender, pero no alimentan
    // récords ni ranking. La misión oficial conserva condiciones iguales.
    if (!run.practiceMode && !isDevPreview) {
      PlayerData.submitRun(run);
      apiClient.submitScore(run);
    }

    // Llegar a la meta termina la aventura; dominar la misión exige 80% de precisión.
    // Con 10 retos, esto equivale a mantener un máximo de 2 errores.
    const mastered = run.won && run.accuracy >= MASTERY_ACCURACY;
    if (!isPracticeSession && !isDevPreview) PlayerData.recordMissionAttempt(planetId, mission.index, run);

    if (mastered && !isPracticeSession && !isDevPreview) {
      PlayerData.markMissionComplete(planetId, mission.index);
      if (PlayerData.countMissionsComplete(planetId, missions.length) >= missions.length) {
        PlayerData.markPlanetComplete(planetId);
      }
    }

    let best = isDevPreview ? 0 : PlayerData.getMissionBest(planetId, mission.index);
    let isRecord = false;
    // Solo una misión dominada puede establecer récord y abrir la siguiente.
    if (mastered && !run.practiceMode && !isPracticeSession && !isDevPreview) {
      ({ best, isRecord } = PlayerData.submitMissionScore(planetId, mission.index, run.score));
      PlayerData.submitPlanetScore(planetId, run.score);
    }
    if (!isDevPreview) finishProgressAttempt({ planetId, missionIndex: mission.index, run, mode: isPracticeSession ? 'practice' : run.practiceMode ? 'adaptive' : 'adventure' });
    const root = mountMenu(this);
    root.classList.add(planetId === 'resta' ? 'tmag-result-resta' : planetId === 'mult' ? 'tmag-result-mult' : planetId === 'division' ? 'tmag-result-division' : 'tmag-result-suma');
    if (planetId === 'resta') root.classList.add('tmag-resta');
    if (planetId === 'mult') root.classList.add('tmag-multiplicacion');
    if (planetId === 'division') root.classList.add('tmag-division');
    header(root, isPracticeSession ? '¡PRÁCTICA TERMINADA!' : run.practiceMode ? (mastered ? '¡PRÁCTICA DOMINADA!' : '¡PRÁCTICA TERMINADA!') : mastered ? '¡MISIÓN DOMINADA!' : run.won ? '¡MISIÓN TERMINADA!' : run.timeLeft <= 0 ? '¡SE ACABÓ EL TIEMPO!' : '¡SIN VIDAS!', `${mission.name} · ${mission.skill}`);
    const panel = element('div', 'tmag-detail');

    if (isPracticeSession) {
      rows(panel, [
        ['Ejercicios completados', `${run.correct} / ${run.total}`],
        ['Respuestas correctas', run.correct],
        ['Respuestas incorrectas', run.wrong],
        ['Precisión', `${run.accuracy}%`],
        ['Objetivo', 'Practicar a tu ritmo'],
      ]);
      const configureAgain = () => go(this, 'MissionScene', () => {
        this.registry.set('adaptivePractice', false);
      });
      panel.append(button('↻ CONFIGURAR NUEVA PRÁCTICA', configureAgain, 'tmag-button tmag-primary'));
      const actions = element('div', 'tmag-actions');
      actions.append(button('← Volver al sistema', () => go(this, planetId === 'resta' ? 'RestaMissionsScene' : planetId === 'mult' ? 'MultiplicacionMissionsScene' : planetId === 'division' ? 'DivisionMissionsScene' : 'SumaMissionsScene')));
      panel.append(actions);
      root.append(panel);
      primaryShortcut(root, configureAgain);
      return;
    }
    const perfectRun = run.won && run.accuracy === 100 && run.wrong === 0;
    const speedBonusText = perfectRun
      ? `⚡ +${formatScore(run.speedBonus || 0)}`
      : 'No aplica';
    rows(panel, [
      ['Puntos antes del bono', formatScore(run.scoreBeforeSpeedBonus ?? run.score)],
      ['Respuestas correctas', run.correct],
      ['Respuestas incorrectas', run.wrong],
      ['Precisión', `${run.accuracy}%`],
      ['Mejor racha', `x${run.bestStreak}`],
      ['Tiempo empleado', `${Number(run.elapsedTime || 0).toFixed(1)}s`],
      ['Bono perfecto de rapidez', speedBonusText],
      ['Puntuación final', formatScore(run.score)],
      ['Modo', run.practiceMode ? 'Práctica inteligente' : 'Misión oficial'],
      ['Meta para dominar', `${MASTERY_ACCURACY}% de precisión`],
      ['Estado de la destreza', mastered ? '✓ DOMINADA' : run.won ? '↻ PRACTICAR DE NUEVO' : 'EN PROGRESO', mastered ? 'tmag-status-mastered' : ''],
      ['Mejor de la misión', best ? formatScore(best) : '—'],
    ]);
    if (run.speedBonus > 0) {
      panel.append(element('p', 'tmag-record', `⚡ ¡BONO PERFECTO DE RAPIDEZ! +${formatScore(run.speedBonus)} ⚡`));
    }
    if (run.won && !mastered) {
      panel.append(element('p', 'tmag-record', `🎯 Lograste ${run.accuracy}% de precisión. Para desbloquear la próxima misión necesitas ${MASTERY_ACCURACY}%. Practica esta destreza y vuelve a intentarlo.`));
    }
    if (isRecord) panel.append(element('p', 'tmag-record', '★ ¡NUEVO RÉCORD! ★'));
    const hasNext = mission.index < missions.length - 1;
    if (mastered && !hasNext) panel.append(element('p', 'tmag-record', `🏆 ¡Completaste todas las misiones de Planeta ${planetId === 'resta' ? 'Resta' : planetId === 'mult' ? 'Multiplicación' : planetId === 'division' ? 'División' : 'Suma'}!`));
    const officialReplay = () => go(this, 'LevelScene', () => this.registry.set('adaptivePractice', false));
    const smartPractice = () => go(this, 'LevelScene', () => this.registry.set('adaptivePractice', true));
    const next = () => go(this, 'MissionScene', () => {
      this.registry.set('adaptivePractice', false);
      this.registry.set('currentMissionIndex', mission.index + 1);
      this.registry.set('currentMissionData', missions[mission.index + 1]);
    });
    if (mastered && hasNext) {
      const map = () => go(this, planetId === 'resta' ? 'RestaMissionsScene' : planetId === 'mult' ? 'MultiplicacionMissionsScene' : planetId === 'division' ? 'DivisionMissionsScene' : 'SumaMissionsScene');
      const notice = element('p', 'tmag-record');
      notice.setAttribute('role', 'status');
      notice.setAttribute('aria-live', 'polite');
      const controls = element('div', '');
      panel.append(notice, controls);
      root.append(panel);
      const access = createMissionContentAccess(this, renderActions);
      const eligible = () => access.allows(mission.index + 1) && PlayerData.isMissionUnlocked(planetId, mission.index + 1);
      const guardedNext = () => {
        if (!eligible()) { renderActions(); return; }
        next(); // MissionScene and backend admission remain unchanged.
      };
      const guardedReplay = () => {
        if (!access.allows(mission.index)) { renderActions(); return; }
        officialReplay();
      };
      const scene = this;
      let primary = map;
      function renderActions() {
        if (scene.menuLeaving) return;
        const allowed = eligible();
        const state = access.getState();
        notice.textContent = allowed ? '' : state === 'loading'
          ? '🎉 ¡Dominaste esta misión! Estamos comprobando el acceso a la siguiente.'
          : state === 'error'
            ? '🎉 ¡Dominaste esta misión! No pudimos comprobar tu acceso. Tu logro se conserva; vuelve al mapa para verificarlo.'
            : state === 'free' || state === 'guest'
              ? '🎉 ¡Dominaste esta misión! La primera misión de cada mundo es gratis. Las siguientes misiones requieren Premium. Un adulto administra el acceso de cada estudiante.'
              : '🎉 ¡Dominaste esta misión! Consulta en el mapa los requisitos académicos de la siguiente misión.';
        controls.replaceChildren();
        primary = allowed ? guardedNext : map;
        controls.append(button(allowed ? '▶ SIGUIENTE MISIÓN' : '← VOLVER AL MAPA', primary, 'tmag-button tmag-primary'));
        const actions = element('div', 'tmag-actions');
        if (access.allows(mission.index)) actions.append(button('↻ REPETIR MISIÓN', guardedReplay));
        if (allowed) actions.append(button('← VOLVER AL MAPA', map));
        if (state === 'error') actions.append(button('Volver a comprobar acceso', access.refresh));
        controls.append(actions);
      }
      renderActions();
      // The root shortcut always follows the currently allowed primary action.
      primaryShortcut(root, () => primary());
      return;
    }
    const needsPractice = !mastered;
    const primary = mastered && hasNext ? next : needsPractice ? smartPractice : officialReplay;
    const primaryLabel = mastered && hasNext
      ? '▶ SIGUIENTE MISIÓN'
      : needsPractice
        ? '🎯 PRÁCTICA INTELIGENTE'
        : '↻ REPETIR MISIÓN';
    panel.append(button(primaryLabel, primary, 'tmag-button tmag-primary'));
    const actions = element('div', 'tmag-actions');
    if (needsPractice) actions.append(button('↻ Repetir misión oficial', officialReplay));
    if (mastered && hasNext) actions.append(button('↻ Repetir', officialReplay));
    actions.append(button('← Mapa de misiones', () => go(this, planetId === 'resta' ? 'RestaMissionsScene' : planetId === 'mult' ? 'MultiplicacionMissionsScene' : planetId === 'division' ? 'DivisionMissionsScene' : 'SumaMissionsScene')));
    panel.append(actions);
    root.append(panel);
    primaryShortcut(root, primary);
  }
}
